/**
 * Server-side user preferences, read as untrusted input. A value written by
 * an older build, or half-written by a load that failed, must be dropped —
 * never allowed to wedge a selector that expects today's shape. Only the
 * keys the frame indexes into are checked; anything else passes through.
 */
import type { PinItem, RecentItem, UserPreferences } from '../services/api';

const isRecord = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const isString = (v: unknown): v is string => typeof v === 'string' && v.length > 0;

const PIN_KINDS: ReadonlySet<string> = new Set<PinItem['kind']>(['page', 'live-model', 'routine', 'agent']);
const RECENT_KINDS: ReadonlySet<string> = new Set<RecentItem['kind']>([
  'conversation', 'file', 'presentation', 'routine', 'run', 'live-model', 'page',
]);

/** A pinned or recent item: a known kind, with an id, a title and a path. */
function items<T extends { kind: string }>(value: unknown, kinds: ReadonlySet<string>): T[] | undefined {
  if (!Array.isArray(value)) return undefined;
  return value.filter(
    (i): i is T =>
      isRecord(i) && kinds.has(i.kind as string) && isString(i.id) && typeof i.title === 'string' && isString(i.path),
  );
}

export function sanitizePreferences(raw: unknown): UserPreferences {
  if (!isRecord(raw)) return {};
  const prefs = { ...raw } as UserPreferences & Record<string, unknown>;

  if (prefs.activeProjectId !== undefined && !isString(prefs.activeProjectId)) delete prefs.activeProjectId;

  const pins = items<PinItem>(raw.pins, PIN_KINDS);
  if (pins) prefs.pins = pins;
  else delete prefs.pins;
  if (prefs.pinsSeeded !== undefined && prefs.pinsSeeded !== true) delete prefs.pinsSeeded;
  if (prefs.pinsSeededFor !== undefined) {
    if (Array.isArray(raw.pinsSeededFor)) prefs.pinsSeededFor = raw.pinsSeededFor.filter(isString);
    else delete prefs.pinsSeededFor;
  }

  const recent = items<RecentItem>(raw.recent, RECENT_KINDS);
  if (recent) prefs.recent = recent;
  else delete prefs.recent;

  if (isRecord(raw.drawer)) {
    const threads = isRecord(raw.drawer.threads)
      ? Object.fromEntries(Object.entries(raw.drawer.threads).filter(([k, v]) => isString(k) && isString(v)))
      : undefined;
    prefs.drawer = {
      ...(typeof raw.drawer.open === 'boolean' ? { open: raw.drawer.open } : {}),
      ...(threads ? { threads: threads as Record<string, string> } : {}),
    };
  } else {
    delete prefs.drawer;
  }

  if (isRecord(raw.frame)) {
    const { sidebarCollapsed, drawerWidth } = raw.frame;
    prefs.frame = {
      ...(typeof sidebarCollapsed === 'boolean' ? { sidebarCollapsed } : {}),
      ...(typeof drawerWidth === 'number' && Number.isFinite(drawerWidth) && drawerWidth > 0 ? { drawerWidth } : {}),
    };
  } else {
    delete prefs.frame;
  }

  for (const key of ['tabsByProject', 'expandedFolders', 'panes'] as const) {
    if (prefs[key] !== undefined && !isRecord(prefs[key])) delete prefs[key];
  }
  return prefs;
}
